import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Box, Button, CircularProgress, IconButton,
  MenuItem, Select, FormControl, Tooltip, Typography, Paper, Chip,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  SwapHoriz as MappingIcon,
  Warning as WarnIcon,
  CheckCircle as OkIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import {
  channelsApi,
  queueApi,
  CRM_LEAD_FIELDS,
  REQUIRED_CRM_FIELDS,
  type Channel,
  type FieldMapping,
} from '../../lib/api/channelsApi'
import { getCustomerTypes } from '../../lib/api/customerTypesApi'
import { themeColors } from '../../app/layout/theme'

/** CRM fields whose value is chosen by the user (static), never read from the payload */
const STATIC_VALUE_FIELDS = ['customer_type_id']

// ── Platform field suggestions per channel type ───────────────────────────────

const DEFAULT_PLATFORM_FIELDS: Record<string, { platform: string; crm: string }[]> = {
  META: [
    { platform: 'full_name',    crm: 'Name' },
    { platform: 'email',        crm: 'Email' },
    { platform: 'phone_number', crm: 'Phone' },
    { platform: 'company_name', crm: 'company_name' },
    { platform: 'city',         crm: 'address' },
    { platform: 'post_code',    crm: 'pincode' },
  ],
  WEBSITE: [
    { platform: 'name',     crm: 'Name' },
    { platform: 'email',    crm: 'Email' },
    { platform: 'phone',    crm: 'Phone' },
    { platform: 'company',  crm: 'company_name' },
    { platform: 'address',  crm: 'address' },
    { platform: 'pincode',  crm: 'pincode' },
    { platform: 'message',  crm: 'NextAction' },
  ],
  WHATSAPP: [
    { platform: 'name',    crm: 'Name' },
    { platform: 'phone',   crm: 'Phone' },
    { platform: 'message', crm: 'NextAction' },
  ],
  INSTAGRAM: [
    { platform: 'full_name',    crm: 'Name' },
    { platform: 'email',        crm: 'Email' },
    { platform: 'phone_number', crm: 'Phone' },
  ],
  GOOGLE: [
    { platform: 'FULL_NAME',    crm: 'Name' },
    { platform: 'EMAIL',        crm: 'Email' },
    { platform: 'PHONE_NUMBER', crm: 'Phone' },
    { platform: 'COMPANY_NAME', crm: 'company_name' },
    { platform: 'CITY',         crm: 'address' },
    { platform: 'POSTAL_CODE',  crm: 'pincode' },
  ],
  REST_API: [
    { platform: 'name',         crm: 'Name' },
    { platform: 'email',        crm: 'Email' },
    { platform: 'phone',        crm: 'Phone' },
    { platform: 'company_name', crm: 'company_name' },
    { platform: 'address',      crm: 'address' },
    { platform: 'gst',          crm: 'gst' },
    { platform: 'pincode',      crm: 'pincode' },
    { platform: 'source',       crm: 'Source' },
    { platform: 'best_time',    crm: 'BestTimeToContact' },
    { platform: 'description',  crm: 'NextAction' },
    { platform: 'warehouse_id', crm: 'warehouse_id' },
    // org_id / lob_id intentionally omitted — fixed at channel creation.
    // customer_type_id intentionally omitted — it is set via a static
    // Customer Type dropdown, never read from the channel payload.
  ],
}

const CRM_FIELD_GROUPS = [
  { label: 'Core Identity',    fields: ['Name', 'Phone', 'Email'] },
  { label: 'Lead Metadata',    fields: ['Source', 'BestTimeToContact', 'NextAction', 'file_type'] },
  { label: 'Company / Address',fields: ['company_name', 'address', 'pincode', 'gst'] },
  { label: 'Tenant Context',   fields: ['warehouse_id', 'customer_type_id'] },
]

// ── Helper ────────────────────────────────────────────────────────────────────

function getCrmFieldMeta(value: string) {
  return CRM_LEAD_FIELDS.find(f => f.value === value)
}

/**
 * Build a flat list of { value, label, dbColumn, required, group } descriptors
 * in group order — used to render <MenuItem> lists without JSX inside callbacks.
 */
function buildGroupedFieldList() {
  return CRM_FIELD_GROUPS.flatMap(group =>
    group.fields.flatMap(fv => {
      const fd = CRM_LEAD_FIELDS.find(f => f.value === fv)
      if (!fd) return []
      return [{ ...fd, group: group.label }]
    })
  )
}

const GROUPED_FIELDS = buildGroupedFieldList()

// ── Main Component ────────────────────────────────────────────────────────────

interface LeadMappingPanelProps {
  channel: Channel
}

export function LeadMappingPanel({ channel }: LeadMappingPanelProps) {
  const qc = useQueryClient()
  const [mappings, setMappings] = useState<FieldMapping[]>(
    channel.fieldMappings?.length ? [...channel.fieldMappings] : []
  )
  const [isDirty, setIsDirty] = useState(false)
  const [detectedFields, setDetectedFields] = useState<string[] | null>(null)
  const [isFetchingFields, setIsFetchingFields] = useState(false)

  // Customer types — same list the lead creation form uses; the selected
  // value is stored as a static mapping value (not read from the payload)
  const { data: customerTypes = [] } = useQuery({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 60_000,
  })

  // Keep local mappings state in sync with server values
  useEffect(() => {
    setMappings(channel.fieldMappings?.length ? [...channel.fieldMappings] : [])
  }, [channel.fieldMappings])

  const platformSuggestions = useMemo(() => {
    const defaultSuggestions = (DEFAULT_PLATFORM_FIELDS[channel.type] ?? []).map(s => s.platform)
    if (detectedFields) {
      return Array.from(new Set([...defaultSuggestions, ...detectedFields]))
    }
    return defaultSuggestions
  }, [channel.type, detectedFields])

  const updateMutation = useMutation({
    mutationFn: () => channelsApi.updateFieldMappings(channel.id, mappings),
    onSuccess: () => {
      toast.success('Field mappings saved')
      setIsDirty(false)
      qc.invalidateQueries({ queryKey: ['channel', channel.id] })
      qc.invalidateQueries({ queryKey: ['channels'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to save mappings'),
  })

  const addRow = () => { setMappings(prev => [...prev, { platformField: '', crmField: '' }]); setIsDirty(true) }
  const removeRow = (i: number) => { setMappings(prev => prev.filter((_, j) => j !== i)); setIsDirty(true) }
  const updateRow = (i: number, key: keyof FieldMapping, val: string) => {
    setMappings(prev => prev.map((m, j) => {
      if (j !== i) return m
      const next = { ...m, [key]: val }
      // Switching the CRM field: static fields never read from the payload,
      // payload fields never carry a static value.
      if (key === 'crmField') {
        if (STATIC_VALUE_FIELDS.includes(val)) next.platformField = ''
        else next.staticValue = undefined
      }
      return next
    }))
    setIsDirty(true)
  }

  const applyDefaults = () => {
    const defaults = DEFAULT_PLATFORM_FIELDS[channel.type] ?? []
    setMappings(defaults.map(d => ({ platformField: d.platform, crmField: d.crm })))
    setIsDirty(true)
  }

  const fetchApiFields = async () => {
    if (!channel.apiBaseUrl) {
      toast.error('API Base URL is required to fetch fields')
      return
    }
    setIsFetchingFields(true)
    try {
      const result = await queueApi.testConnection(channel.apiBaseUrl, channel.accessToken ?? '')
      if (result.ok && result.keys) {
        setDetectedFields(result.keys)
        toast.success(`Detected ${result.keys.length} fields from your API`)
      } else {
        toast.error(result.message || 'Failed to detect fields')
      }
    } catch {
      toast.error('Failed to connect to API')
    } finally {
      setIsFetchingFields(false)
    }
  }

  const handleAddOrSelectKey = (key: string) => {
    const emptyRowIndex = mappings.findIndex(m => !m.platformField && !STATIC_VALUE_FIELDS.includes(m.crmField))
    if (emptyRowIndex !== -1) {
      updateRow(emptyRowIndex, 'platformField', key)
      toast.info(`Assigned key "${key}" to mapping row`)
    } else {
      setMappings(prev => [...prev, { platformField: key, crmField: '' }])
      setIsDirty(true)
      toast.info(`Added mapping row for key "${key}"`)
    }
  }

  // Validation
  const mappedCrmFields = useMemo(() => new Set(mappings.map(m => m.crmField).filter(Boolean)), [mappings])
  const unmappedRequired = REQUIRED_CRM_FIELDS.filter(f => !mappedCrmFields.has(f))
  const isValid = unmappedRequired.length === 0
  const mappedCount = mappings.filter(m => m.crmField && (m.platformField || m.staticValue)).length
  const totalFields = CRM_LEAD_FIELDS.length

  // ── Pre-compute per-row Select renderValue labels ────────────────────────────
  // (computed here, not inside JSX, so TypeScript parses them cleanly)
  const rowSelectedLabels: string[] = mappings.map(m => {
    if (!m.crmField) return ''
    const fd = getCrmFieldMeta(m.crmField)
    return fd ? `${fd.label}  ·  ${fd.dbColumn}${fd.required ? ' *' : ''}` : m.crmField
  })

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MappingIcon sx={{ fontSize: 18, color: themeColors.primary }} />
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary }}>
            Lead Field Mapping Engine
          </Typography>
          <Chip
            label={`${mappedCount} / ${totalFields} fields mapped`}
            size="small"
            sx={{
              height: 20, fontSize: '10.5px', fontWeight: 600,
              bgcolor: mappedCount > 0 ? '#f0fdf4' : '#f8f9fa',
              color: mappedCount > 0 ? '#15803d' : themeColors.text.muted,
              border: `1px solid ${mappedCount > 0 ? '#bbf7d0' : themeColors.border}`,
              '& .MuiChip-label': { px: '6px' },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {(DEFAULT_PLATFORM_FIELDS[channel.type]?.length ?? 0) > 0 && (
            <Button size="small" variant="outlined" onClick={applyDefaults}
              sx={{
                textTransform: 'none', fontSize: '12px', fontWeight: 600,
                borderColor: themeColors.border, color: themeColors.text.secondary,
                '&:hover': {
                  borderColor: themeColors.primary, color: themeColors.primary,
                  bgcolor: themeColors.primaryLight + '15',
                },
              }}
            >
              Load defaults
            </Button>
          )}
          <Button size="small" variant="contained"
            onClick={() => updateMutation.mutate()}
            disabled={!isDirty || updateMutation.isPending}
            startIcon={updateMutation.isPending
              ? <CircularProgress size={12} sx={{ color: '#fff' }} />
              : <SaveIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none', fontSize: '12px', fontWeight: 700,
              bgcolor: themeColors.primary, boxShadow: 'none',
              '&:hover': { bgcolor: themeColors.primaryDark, boxShadow: 'none' },
            }}
          >
            Save Mappings
          </Button>
        </Box>
      </Box>

      {/* Info banner */}
      <Box sx={{ p: 1.5, mb: 2, borderRadius: '8px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <Typography sx={{ fontSize: '11.5px', color: '#1e40af', lineHeight: 1.65 }}>
          Map your <strong>{channel.type}</strong> payload field names to the exact{' '}
          <code style={{ fontFamily: 'monospace', fontSize: '11px', background: '#dbeafe', padding: '1px 4px', borderRadius: 3 }}>
            public.leads
          </code>{' '}
          column names. Fields marked <strong style={{ color: '#dc2626' }}>*</strong>{' '}
          (<strong>Name</strong>, <strong>Phone</strong>) are required.
        </Typography>
      </Box>

      {/* Fetch fields section */}
      {channel.type === 'REST_API' && (
        <Box sx={{ mb: 2.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={fetchApiFields}
            disabled={isFetchingFields}
            startIcon={isFetchingFields ? <CircularProgress size={12} /> : <MappingIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none', fontSize: '12px', fontWeight: 600,
              borderColor: themeColors.primary, color: themeColors.primary,
              '&:hover': { bgcolor: themeColors.primaryLight, borderColor: themeColors.primary }
            }}
          >
            {isFetchingFields ? 'Fetching Fields…' : 'Fetch Fields from API Response'}
          </Button>

          {detectedFields && (
            <Paper elevation={0} sx={{ p: 2, mt: 1.5, border: `1px solid ${themeColors.border}`, borderRadius: '8px', bgcolor: '#fdfdfd' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: themeColors.text.primary, mb: 0.5 }}>
                Detected API Keys ({detectedFields.length})
              </Typography>
              {detectedFields.length === 0 ? (
                <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted }}>
                  Connected successfully, but no leads were returned to extract keys from. Please ensure there is at least one lead in the external system.
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontSize: '11.5px', color: themeColors.text.muted, mb: 1.5 }}>
                    Click any key below to quickly map it (assigns to the first empty row or adds a new row):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {detectedFields.map(key => {
                      const isMapped = mappings.some(m => m.platformField === key)
                      return (
                        <Chip
                          key={key}
                          label={key}
                          size="small"
                          onClick={() => handleAddOrSelectKey(key)}
                          disabled={isMapped}
                          sx={{
                            fontSize: '11.5px', fontWeight: 500, cursor: 'pointer',
                            bgcolor: isMapped ? '#f1f5f9' : `${themeColors.primary}12`,
                            color: isMapped ? '#94a3b8' : themeColors.primary,
                            border: `1px solid ${isMapped ? '#cbd5e1' : themeColors.primary}30`,
                            '&:hover': {
                              bgcolor: isMapped ? '#f1f5f9' : themeColors.primary,
                              color: isMapped ? '#94a3b8' : '#fff'
                            }
                          }}
                        />
                      )
                    })}
                  </Box>
                </>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* Required fields warning */}
      {isDirty && !isValid && (
        <Box sx={{
          p: 1.5, mb: 2, borderRadius: '8px', bgcolor: '#fff7ed', border: '1px solid #fed7aa',
          display: 'flex', alignItems: 'flex-start', gap: 1,
        }}>
          <WarnIcon sx={{ fontSize: 16, color: '#c2410c', flexShrink: 0, mt: '1px' }} />
          <Box>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#c2410c' }}>
              Required fields not yet mapped:
            </Typography>
            <Typography sx={{ fontSize: '11.5px', color: '#9a3412', mt: 0.25 }}>
              {unmappedRequired.map(f => getCrmFieldMeta(f)?.label ?? f).join(', ')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Column headers */}
      {mappings.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 24px 1fr auto', gap: 1, mb: 1, px: 0.5 }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: themeColors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Platform Field (incoming)
          </Typography>
          <Box />
          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: themeColors.text.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            CRM Field (public.leads column)
          </Typography>
          <Box sx={{ width: 32 }} />
        </Box>
      )}

      {/* Empty state */}
      {mappings.length === 0 && (
        <Paper elevation={0} sx={{
          p: 3, textAlign: 'center', borderRadius: '10px',
          border: `1.5px dashed ${themeColors.border}`,
        }}>
          <MappingIcon sx={{ fontSize: 32, color: themeColors.text.muted, opacity: 0.4, mb: 1 }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.secondary }}>
            No field mappings yet
          </Typography>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.muted, mt: 0.5 }}>
            Click "Load defaults" for pre-built {channel.type} mappings, or add rows manually.
          </Typography>
        </Paper>
      )}

      {/* Mapping rows */}
      {mappings.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {mappings.map((mapping, index) => {
            const meta = getCrmFieldMeta(mapping.crmField)
            const isRequired = meta?.required ?? false
            const isStaticRow = STATIC_VALUE_FIELDS.includes(mapping.crmField)
            const isMapped = !!mapping.crmField && (isStaticRow ? !!mapping.staticValue : !!mapping.platformField)

            return (
              <Box key={index} sx={{
                display: 'grid', gridTemplateColumns: '1fr 24px 1fr auto',
                gap: 1, alignItems: 'center', p: 1, borderRadius: '8px',
                bgcolor: isRequired && !isMapped ? '#fff7ed' : '#fafafa',
                border: `1px solid ${isRequired && !isMapped ? '#fed7aa' : themeColors.border}`,
              }}>

                {/* Platform field (payload key) — or a static Customer Type dropdown */}
                {isStaticRow ? (
                  <FormControl fullWidth size="small">
                    <Select
                      value={mapping.staticValue ?? ''}
                      onChange={e => updateRow(index, 'staticValue', String(e.target.value))}
                      displayEmpty
                      renderValue={() => {
                        const ct = customerTypes.find(c => String(c.id) === String(mapping.staticValue ?? ''))
                        return (
                          <Typography sx={{ fontSize: '12.5px', fontWeight: ct ? 600 : 400, color: ct ? themeColors.text.primary : themeColors.text.muted }}>
                            {ct ? ct.name : 'Select customer type…'}
                          </Typography>
                        )
                      }}
                      sx={{
                        borderRadius: '6px', fontSize: '12.5px', bgcolor: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary, borderWidth: '1px' },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ fontSize: '12px', color: themeColors.text.muted }}>
                        Select customer type...
                      </MenuItem>
                      {customerTypes.map(ct => (
                        <MenuItem key={ct.id} value={String(ct.id)} sx={{ fontSize: '12.5px', py: '5px' }}>
                          {ct.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                <Box>
                  <input
                    list={`pf-list-${index}`}
                    value={mapping.platformField}
                    onChange={e => updateRow(index, 'platformField', e.target.value)}
                    placeholder="e.g. full_name"
                    style={{
                      width: '100%', height: 34, padding: '0 10px',
                      borderRadius: 6, border: `1px solid ${themeColors.border}`,
                      fontSize: 12.5, color: themeColors.text.primary,
                      outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
                      background: '#fff',
                    }}
                    onFocus={e => { e.target.style.borderColor = themeColors.primary }}
                    onBlur={e => { e.target.style.borderColor = themeColors.border }}
                  />
                  <datalist id={`pf-list-${index}`}>
                    {platformSuggestions.map(s => <option key={s} value={s} />)}
                  </datalist>
                </Box>
                )}

                {/* Arrow */}
                <Typography sx={{ fontSize: '14px', color: themeColors.text.muted, textAlign: 'center' }}>
                  {'>'}
                </Typography>

                {/* CRM field dropdown — pre-built grouped items, no JSX in callbacks */}
                <FormControl fullWidth size="small">
                  <Select
                    value={mapping.crmField || ''}
                    onChange={e => updateRow(index, 'crmField', e.target.value)}
                    displayEmpty
                    renderValue={() => {
                      const label = rowSelectedLabels[index]
                      if (!label) {
                        return (
                          <Typography sx={{ fontSize: '12.5px', color: themeColors.text.muted }}>
                            Select DB column
                          </Typography>
                        )
                      }
                      return (
                        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: themeColors.text.primary }}>
                          {label}
                        </Typography>
                      )
                    }}
                    sx={{
                      borderRadius: '6px', fontSize: '12.5px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.border },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: themeColors.primary },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: themeColors.primary, borderWidth: '1px',
                      },
                    }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: '12px', color: themeColors.text.muted }}>
                      Select DB column...
                    </MenuItem>

                    {/* Group headers + items rendered from plain data array — no JSX-in-callback */}
                    {GROUPED_FIELDS.map((fd, fi) => {
                      const isFirstInGroup = fi === 0 || GROUPED_FIELDS[fi - 1].group !== fd.group
                      const alreadyUsed = mappings.some((m, j) => j !== index && m.crmField === fd.value)

                      return [
                        isFirstInGroup ? (
                          <MenuItem
                            key={`hdr-${fd.group}`}
                            disabled
                            sx={{
                              fontSize: '10px', fontWeight: 700, color: themeColors.text.muted,
                              textTransform: 'uppercase', letterSpacing: '0.6px',
                              py: '3px', mt: '2px', bgcolor: '#f8f9fa', opacity: '1 !important',
                            }}
                          >
                            {fd.group}
                          </MenuItem>
                        ) : null,
                        <MenuItem
                          key={fd.value}
                          value={fd.value}
                          disabled={alreadyUsed}
                          sx={{ fontSize: '12.5px', py: '5px' }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '12.5px', fontWeight: 500 }}>
                                {fd.label}
                              </Typography>
                              {fd.required && (
                                <Typography sx={{ fontSize: '11px', color: '#dc2626', fontWeight: 700 }}>
                                  *
                                </Typography>
                              )}
                              {alreadyUsed && (
                                <Chip label="mapped" size="small" sx={{
                                  height: 14, fontSize: '9px', bgcolor: '#f0fdf4', color: '#15803d',
                                  border: '1px solid #bbf7d0', '& .MuiChip-label': { px: '4px' },
                                }} />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: themeColors.text.muted }}>
                              {fd.dbColumn}
                            </Typography>
                          </Box>
                        </MenuItem>,
                      ]
                    })}
                  </Select>
                </FormControl>

                {/* Status + remove */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  {isMapped
                    ? <OkIcon sx={{ fontSize: 14, color: '#10b981' }} />
                    : <WarnIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                  }
                  <Tooltip title="Remove row">
                    <IconButton size="small" onClick={() => removeRow(index)}
                      sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fee2e2' } }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )
          })}
        </Box>
      )}

      {/* Add row */}
      <Box
        onClick={addRow}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          p: 1, borderRadius: '8px', cursor: 'pointer', mt: 1.5,
          border: `1.5px dashed ${themeColors.border}`, color: themeColors.primary,
          '&:hover': { bgcolor: themeColors.primaryLight + '15', borderColor: themeColors.primary },
          transition: 'all 0.15s',
        }}
      >
        <AddIcon sx={{ fontSize: 15 }} />
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600 }}>Add mapping row</Typography>
      </Box>

      {/* DB schema reference */}
      <Box sx={{ mt: 3, p: 2, borderRadius: '10px', bgcolor: '#fafafa', border: `1px solid ${themeColors.border}` }}>
        <Typography sx={{
          fontSize: '11.5px', fontWeight: 700, color: themeColors.text.secondary,
          mb: 1.25, textTransform: 'uppercase', letterSpacing: '0.5px',
        }}>
          public.leads — mappable columns
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0.75 }}>
          {CRM_LEAD_FIELDS.map(f => {
            const isMapped = mappedCrmFields.has(f.value)
            return (
              <Box key={f.value} sx={{
                display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5,
                borderRadius: '6px', bgcolor: isMapped ? '#f0fdf4' : '#fff',
                border: `1px solid ${isMapped ? '#bbf7d0' : themeColors.border}`,
              }}>
                {isMapped
                  ? <OkIcon sx={{ fontSize: 12, color: '#10b981', flexShrink: 0 }} />
                  : <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: themeColors.border, flexShrink: 0 }} />
                }
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: isMapped ? '#15803d' : themeColors.text.primary }} noWrap>
                      {f.label}
                    </Typography>
                    {f.required && (
                      <Typography sx={{ fontSize: '10px', color: '#dc2626', fontWeight: 700 }}>*</Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '10px', fontFamily: 'monospace', color: themeColors.text.muted }} noWrap>
                    {f.dbColumn}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Box>
        <Typography sx={{ fontSize: '10.5px', color: themeColors.text.muted, mt: 1.25 }}>
          * Required. System columns (Id, OwnerId, Status, CreatedAt, etc.) are auto-managed and not listed.
        </Typography>
      </Box>
    </Box>
  )
}
